import {
  FETCH_LEAVE_REQUESTS_REQUEST,
  FETCH_LEAVE_REQUESTS_SUCCESS,
  FETCH_LEAVE_REQUESTS_FAIL,
} from "../../constants/Leave/LeaveRequestConstants";
import LeaveRequestService from "../../service/Leave/LeaveRequestService";
import { extractRows, toDateKey, assertOk, friendlyError } from "../../service/Leave/leaveHelpers";

const STATUS_LABEL = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
  CANCELED: "Cancelled",
};

const errMsg = (error, fallback) =>
  friendlyError(error.response?.data?.Result || error.response?.data?.message || error.message, fallback);

const mapRow = (item, idx) => {
  const status = String(item.HLF_STATUS ?? "").trim().toUpperCase();
  return {
    key: `${item.HLF_FORM_NO ?? "row"}-${idx}`,
    formNo: String(item.HLF_FORM_NO ?? "").trim(),
    serviceNo: String(item.HLF_EMP_SERVICE_NO ?? "").trim(),
    leaveTypeId: String(item.HLF_LEAVE_TYPE_ID ?? "").trim(),
    from: toDateKey(item.HLF_FROM_DATE),
    to: toDateKey(item.HLF_TO_DATE),
    days: Number(item.HLF_TOTAL_DAYS) || 0,
    status: STATUS_LABEL[status] || status,
    appliedDate: toDateKey(item.HLF_APPLIED_DATE),
    formId: String(item.HLF_FORM_ID ?? "").trim(),
    reason: String(item.HLF_REASON ?? "").trim(),
    contactNo: String(item.HLF_CONTACT_NO ?? "").trim(),
    attachmentPath: String(item.HLF_ATTACHMENT_PATH ?? "").trim(),
    halfDay: String(item.HLF_HALF_DAY ?? "").trim().toUpperCase() === "Y",
    halfDayType: String(item.HLF_HALF_DAY_TYPE ?? "").trim(),
    approvedBy: String(item.HLF_APPROVED_BY ?? "").trim(),
    rejectReason: String(item.HLF_REJECT_REASON ?? "").trim(),
  };
};

// Loads the requests of every status given (three calls by default; each status is its own endpoint call)
export const fetchLeaveRequests =
  (statuses = ["PENDING", "APPROVED", "REJECTED", "CANCELLED"]) =>
  async (dispatch) => {
    try {
      dispatch({ type: FETCH_LEAVE_REQUESTS_REQUEST });
      const bodies = await Promise.all(
        statuses.map(async (s) => {
          try {
            return assertOk(await LeaveRequestService.getByStatus(s));
          } catch (e) {
            if (s === "CANCELLED") return []; // optional list: don't break the page if the server has none
            throw e;
          }
        })
      );
      const data = bodies.flatMap((body) => extractRows(body)).map(mapRow);
      dispatch({ type: FETCH_LEAVE_REQUESTS_SUCCESS, payload: data });
    } catch (error) {
      dispatch({ type: FETCH_LEAVE_REQUESTS_FAIL, payload: errMsg(error, "Failed to fetch leave requests") });
    }
  };

// Approve / reject one request, then reload the lists. Returns { ok, message }
const decide = (kind, payload) => async (dispatch) => {
  try {
    assertOk(await LeaveRequestService.decide(kind, payload));
    await dispatch(fetchLeaveRequests());
    return { ok: true };
  } catch (error) {
    return { ok: false, message: errMsg(error, `Failed to ${kind.toLowerCase()} leave request`) };
  }
};

export const approveLeave = (payload) => decide("APPROVE", payload);
export const rejectLeave = (payload) => decide("REJECT", payload);
export const cancelLeave = (payload) => decide("CANCEL", payload);

// ── Leave Request tab ───────────────────────────────────────

const SKIP_STATUSES = ["CANCELLED", "CANCELED", "REJECTED", "DECLINED"];

// All requests of one employee (returned, not stored in Redux)
export const getEmployeeLeaves = (serviceNo) => async () => {
  const body = assertOk(await LeaveRequestService.getByEmployee(serviceNo));
  return extractRows(body).map(mapRow);
};

// Same two checks as DGES: duplicate Form No, then overlapping dates
export const findLeaveClash = (existing, { formNo, start, end }) => {
  if (existing.some((l) => l.formNo === formNo)) {
    return { type: "formNo", message: `Leave Form No "${formNo}" already exists.` };
  }
  const clash = existing.find(
    (l) =>
      !SKIP_STATUSES.includes(String(l.status).toUpperCase()) &&
      l.from && l.to && start <= l.to && end >= l.from
  );
  if (clash) {
    const show = (k) =>
      new Date(`${k}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const status = String(clash.status || "Pending").toLowerCase();
    const when = clash.from === clash.to ? `on ${show(clash.from)}` : `from ${show(clash.from)} to ${show(clash.to)}`;
    const article = status === "approved" ? "approved leave" : `a ${status} leave request`;
    const advice =
      status === "pending"
        ? "Please choose different dates, or approve/reject the existing request first."
        : "Please choose different dates.";
    return {
      type: "dateRange",
      message: `This employee already has ${article} ${when} (Form #${clash.formNo}), which overlaps these dates. ${advice}`,
    };
  }
  return null;
};

// Returns { ok, message }. The new request stays Pending until someone approves it in the Approval tab.
export const submitLeaveRequest = (payload) => async (dispatch) => {
  try {
    const body = await LeaveRequestService.submit(payload);
    let message = "Leave request submitted successfully!";
    if (typeof body === "string") {
      if (!/success/i.test(body)) throw new Error(body);
      message = body;
    } else {
      assertOk(body);
      if (typeof body?.Result === "string" && body.Result.trim()) message = body.Result.trim();
    }

    await dispatch(fetchLeaveRequests()); // keeps the Approval tab and its pending count current
    return { ok: true, message };
  } catch (error) {
    return {
      ok: false,
      message: friendlyError(
        error.response?.data?.Result || error.response?.data?.message || error.message,
        "Failed to submit leave request. Please try again.",
        "This Leave Form No. is already used. Please enter a different Form No."
      ),
    };
  }
};